import { Actions, ofType, createEffect } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { createAction, props, on, createSelector } from '@ngrx/store';
import { EntityStoreFactory, IGenericEntityState } from '@pit/core-store';
import { IDtoResourceFault } from '@pit/frontend-model';
import { EntityDataServiceAccessorService } from '@pit/core-data';
import { UtilsMisc } from '@pit/core';
import { IDtoResourceFaultEntityDataService } from '../../module.data/services/interfaces/dto.resource.fault.entity.data.service.interface';

export class DtoResourceFaultStoreFactory extends EntityStoreFactory<IDtoResourceFault> {

    private static _instance: DtoResourceFaultStoreFactory = null;

    public static get instance(): DtoResourceFaultStoreFactory {
        if (this._instance === null) {
            this._instance = new DtoResourceFaultStoreFactory();
        }

        return this._instance;
    }

    constructor() {
        super('DtoResourceFault', 'dto.resource.fault');
    }

    protected override doCreateActions(): void {
        super.doCreateActions();

        this.entityActions.add('get.by.mandator.resourcetype', createAction(
            'Get ResourceFaults By Mandator ResourceType',
            props<{ mandator: string, resourceType: string }>()
        ));
        this.entityActions.add('get.by.mandator.resourcetype.success', createAction(
            'Get ResourceFaults By Mandator ResourceType Success',
            props<{ items: IDtoResourceFault[] }>()
        ));
        this.entityActions.add('get.by.mandator.resourcetype.failure', createAction(
            'Get ResourceFaults By Mandator ResourceType Failure',
            props<{ error: any }>()
        ));
    }

    protected override doCreateEffects(): void {
        super.doCreateEffects();

        this.entityEffects.add('get.by.mandator.resourcetype', {
            effect: (actions$: Actions, dataServiceAccessor: EntityDataServiceAccessorService) => {
                return createEffect(() =>
                    actions$.pipe(
                        ofType(this.getAction('get.by.mandator.resourcetype')),
                        map(action => action),
                        switchMap(action =>
                            (dataServiceAccessor.getEntityDataService<IDtoResourceFault>(this.dataAccessName) as IDtoResourceFaultEntityDataService)
                                .getForMandatorByResourceType$(action.mandator, action.resourceType).pipe(
                                    map(items => this.getAction('get.by.mandator.resourcetype.success')({ items: items })),
                                    catchError(error => of(this.getAction('get.by.mandator.resourcetype.failure')({ error: error })))
                                )
                        )
                    )
                );
            }
        });
    }

    protected override doCreateSelectors() {
        super.doCreateSelectors();


        this.entitySelectors.add('get.for.resource.type', (resourceType: string) => createSelector(
            this.getSelector('get.all.entities'),
            (items: IDtoResourceFault[]) => items.filter(x => (UtilsMisc.isNullOrUndefined(x.penalties.find(y => y.resourceType.code === resourceType)) === false)).sort((a, b) => {
                let result: number = a.type.id - b.type.id;
                if (result === 0) {
                    result = a.code.localeCompare(b.code);
                }

                return result;
            })
        ));
    }

    protected override doGetReducers<S extends IGenericEntityState<IDtoResourceFault>>(): any {
        const result: any = super.doGetReducers();

        result.push(
            on(
                this.getAction('get.by.mandator.resourcetype'),
                (state: any, { showLoadingIndicator }) => {
                    return {
                        ...state,
                        loading: ((showLoadingIndicator === true) ? true : state.loading)
                    };
                }
            )
        );
        result.push(
            on(
                this.getAction('get.by.mandator.resourcetype.failure'),
                (state: any) => {
                    return {
                        ...state,
                        loading: ((state.loading === true) ? false : state.loading)
                    };
                }
            )
        );
        result.push(
            on(this.getAction('get.by.mandator.resourcetype.success'), (state: any, { items }) => {
                const res = this.entityAdapter.setAll(items, {
                    ...state,
                    loading: false,
                    selectedItem: null
                });
                return res;
            })
        );

        return result;
    }

}
